import { useEffect, useRef, useState } from "react";
import { Map as MLMap, Marker, Popup, LngLatBounds, NavigationControl, setWorkerUrl } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";

// MapLibre v6: worker servito come file statico da /public (sicuro in dev e produzione)
// NOTA: estensione .js obbligatoria — il server live serve .mjs come octet-stream e il browser rifiuta il worker
setWorkerUrl("/maplibre-gl-worker.js");
import { imgSrc, formatPrice, pickLang } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

// Mappa vettoriale OpenFreeMap (gratuita, senza API key): colori classici + etichette multilingua
const STYLE_URL = "https://tiles.openfreemap.org/styles/liberty";

// Cambia la lingua delle etichette (città, capitali, strade...) seguendo la lingua del sito
const setMapLanguage = (map, lang) => {
  if (!map.isStyleLoaded()) return;
  for (const layer of map.getStyle().layers) {
    if (layer.type !== "symbol" || !layer.layout || !layer.layout["text-field"]) continue;
    map.setLayoutProperty(layer.id, "text-field", [
      "coalesce",
      ["get", `name:${lang}`],
      ["get", `name_${lang}`],
      ["get", "name"],
    ]);
  }
};

const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

export const PropertyMap = ({ properties, height = "520px", center = [41.9028, 12.4834], zoom = 12, scrollWheel = true }) => {
  const { t, lang } = useLang();
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const [failed, setFailed] = useState(false);
  const withCoords = properties.filter((p) => p.lat && p.lng);

  // Inizializza la mappa una sola volta; in caso di errore il resto della pagina resta intatto
  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    try {
      const map = new MLMap({
        container: containerRef.current,
        style: STYLE_URL,
        center: [center[1], center[0]],
        zoom,
        attributionControl: { compact: true },
      });
      map.addControl(new NavigationControl({ showCompass: false }), "top-left");
      if (!scrollWheel) map.scrollZoom.disable();
      map.on("load", () => setMapLanguage(map, lang));
      map.on("error", (e) => console.error("Errore mappa:", e?.error || e));
      mapRef.current = map;
    } catch (e) {
      console.error("Mappa non disponibile:", e);
      setFailed(true);
    }
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Le etichette seguono la lingua del sito
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (map.isStyleLoaded()) setMapLanguage(map, lang);
    else map.once("load", () => setMapLanguage(map, lang));
  }, [lang]);

  // Pin + inquadratura automatica (i pin sono elementi DOM: non serve attendere il caricamento dello stile)
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];
    const bounds = new LngLatBounds();
    withCoords.forEach((p) => {
      const el = document.createElement("div");
      el.className = "rc-pin";
      el.innerHTML = "<span>RC</span>";
      el.style.cursor = "pointer";
      const img = p.images?.[0] ? imgSrc(p.images[0]) : "";
      const popupHtml = `
        <a href="/immobili/${p.id}" style="display:block;text-decoration:none;color:inherit;width:220px">
          ${img ? `<img src="${esc(img)}" alt="${esc(pickLang(p, "title", lang))}" style="width:100%;height:120px;object-fit:cover" />` : ""}
          <div style="padding:10px 12px 12px">
            <div style="font-weight:600;font-size:14px;line-height:1.3;color:#000000">${esc(pickLang(p, "title", lang))}</div>
            <div style="font-size:12px;color:#5B6670;margin-top:4px">${esc(p.zone)}${p.city ? ", " + esc(p.city) : ""} · ${esc(p.mq)} ${esc(t("card.mq"))}</div>
            <div style="font-family:'IBM Plex Mono',monospace;font-size:14px;font-weight:600;color:#2E2E2E;margin-top:4px">${esc(formatPrice(p, lang))}</div>
          </div>
        </a>`;
      const popup = new Popup({ offset: 24, maxWidth: "250px" }).setHTML(popupHtml);
      const marker = new Marker({ element: el, anchor: "bottom" }).setLngLat([p.lng, p.lat]).setPopup(popup).addTo(map);
      markersRef.current.push(marker);
      bounds.extend([p.lng, p.lat]);
    });
    if (withCoords.length > 1) map.fitBounds(bounds, { padding: 45, maxZoom: 14, duration: 0 });
    else if (withCoords.length === 1) {
      map.setCenter([withCoords[0].lng, withCoords[0].lat]);
      map.setZoom(14);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properties, lang]);

  if (failed) {
    return (
      <div data-testid="property-map" style={{ height }} className="w-full border border-[#E0E3E6] overflow-hidden flex items-center justify-center bg-[#F1F2F4]">
        <p className="text-sm text-[#5B6670] font-mono-d">Mappa momentaneamente non disponibile</p>
      </div>
    );
  }

  return <div data-testid="property-map" ref={containerRef} style={{ height }} className="w-full border border-[#E0E3E6] overflow-hidden" />;
};
